/**
 * The passenger-day allocation engine.
 *
 * Unchanged from V2 in shape: traffic runs both ways, each direction's observed
 * demand is ORIGINATING demand, the full fare is captured once on the outward
 * leg, and returns fill the seats left over at no additional fare.
 *
 * V3 adds ITINERARIES. A demand block is no longer matched to a single train but
 * to the cheapest feasible *journey*, which in Mode B may be two legs with a
 * change at Grosmont. Everything that follows from that — the connection wait,
 * the platform queue, the share of people who simply refuse to change — falls
 * out of the same machinery, so Mode A and Mode B are scored by one engine.
 *
 * ONE DELIBERATE CORRECTNESS CHANGE FROM V2. V2 formed its homeward trainloads
 * per DEPARTURE and sent everyone back over the train's full length, so a
 * Pickering->Goathland passenger on a Whitby train was routed home from Whitby.
 * V3 forms them per JOURNEY, so people return from where they actually went.
 * Mode A totals therefore shift slightly against V2; that is a fix, not drift.
 */
import type {
  ClassDemand, Departure, EngineParams, Network, PerDep, Scenario, Slot,
  StayGroup, StrandedGroup, TransferNode, TransferSummary, TransferWait,
} from './types'
import { fmtMin, toMin } from './demand'
import { crossesTransfer, groupKeyFor, journeyMin, routeLabel } from './network'

/** How long a PERSON needs between stepping off one train and boarding
 *  another at the same station. Distinct from the loco/set turnaround. */
export const PASSENGER_MCT_MIN = 5

export const ELASTICITY = -0.3
export const HEADWAY_MIN = 45
export const STRANDED_OK = 5

/**
 * LEGACY seats heuristic (64/coach, Autocar 48) — D6 killed this as the source
 * of capacity: real seats are a MEASURED property of the assigned carriage set
 * (Departure.capacity via applySetPlan; see stock.setSeats). This survives only
 * as the fallback for manual or unassigned legs, and as the seat estimate for a
 * user-added set with no measured capacity.
 */
export const capOf = (coaches: number): number =>
  coaches < 0 ? 0 : coaches === 0 ? 48 : coaches * 64

/** The seats a departure actually offers: its assigned set's measured capacity,
 *  else the legacy coach heuristic. */
export const depSeats = (d: { coaches: number; capacity?: number }): number =>
  d.capacity ?? capOf(d.coaches)

export const setLabel = (coaches: number): string =>
  coaches === 0 ? 'Autocar' : `${coaches} coaches`

export const gbp = (v: number): string => `£${Math.round(v).toLocaleString('en-GB')}`
export const SET_SIZES = [0, 2, 3, 4, 5, 6, 7, 8]

/** Can this train carry a passenger travelling o->d? */
export const covers = (dep: { o: number; d: number }, o: number, d: number): boolean =>
  Math.min(dep.o, dep.d) <= Math.min(o, d) &&
  Math.max(dep.o, dep.d) >= Math.max(o, d) &&
  Math.sign(dep.d - dep.o) === Math.sign(d - o)

interface DepState extends Departure {
  capacity: number
  free: number
  allocated: number
  outAllocated: number
  outRevenue: number
}

/** A journey: one leg, or two with a change at a transfer node. */
interface Itinerary {
  legs: DepState[]
  depMin: number
  arrMin: number
  waitMin: number
  transfer: TransferNode | null
  transferStation: number
  /** Generalised-time cost beyond the ride itself (penalty + weighted wait). */
  penalty: number
}

/** Only ever offer a passenger the next few connections, not one four hours on. */
const MAX_CONNECTIONS_PER_LEG = 3

function buildItineraries(net: Network, states: DepState[]): Map<string, Itinerary[]> {
  const byOD = new Map<string, Itinerary[]>()
  // Positioning moves run empty; they cost money and paths but carry nobody.
  const usable = states.filter((s) => !s.positioning)

  const push = (o: number, d: number, it: Itinerary) => {
    const k = `${o}>${d}`
    const arr = byOD.get(k)
    if (arr) arr.push(it)
    else byOD.set(k, [it])
  }

  const n = net.stations.length
  for (let o = 0; o < n; o++) {
    for (let d = 0; d < n; d++) {
      if (o === d) continue
      // Direct rides.
      for (const dep of usable) {
        if (!covers(dep, o, d)) continue
        push(o, d, {
          legs: [dep],
          depMin: dep.minutes,
          arrMin: dep.minutes + journeyMin(net, dep.o, d),
          waitMin: 0,
          transfer: null,
          transferStation: -1,
          penalty: 0,
        })
      }
      // Connections, only where the network actually declares a transfer node
      // on this journey. In Mode A this loop never fires, so behaviour is
      // exactly as before.
      const t = crossesTransfer(net, o, d)
      if (!t) continue
      const x = t.station
      const firstLegs = usable.filter((dep) => covers(dep, o, x))
      const secondLegs = usable.filter((dep) => covers(dep, x, d))
      for (const a of firstLegs) {
        const arrA = a.minutes + journeyMin(net, a.o, x)
        const feasible = secondLegs
          .filter((b) => b.minutes >= arrA + t.mctMin)
          .sort((p, q) => p.minutes - q.minutes)
          .slice(0, MAX_CONNECTIONS_PER_LEG)
        for (const b of feasible) {
          const wait = b.minutes - arrA
          push(o, d, {
            legs: [a, b],
            depMin: a.minutes,
            arrMin: b.minutes + journeyMin(net, x, d),
            waitMin: wait,
            transfer: t,
            transferStation: x,
            penalty: t.penaltyMin + t.waitWeight * wait,
          })
        }
      }
    }
  }
  return byOD
}

const freeOn = (it: Itinerary): number => Math.min(...it.legs.map((l) => l.free))

/**
 * Run one day class against a timetable.
 */
export function runScenario(
  net: Network,
  north: Slot[],
  south: Slot[],
  deps: Departure[],
  p: EngineParams
): Scenario {
  const { toleranceMin, recapture, fareMult, dwellMin, returnMode, opts } = p
  const fe = Math.pow(fareMult, ELASTICITY)

  const mkState = (dir: string): DepState[] => deps
    .filter((x) => x.enabled && x.direction === dir && Number.isFinite(x.minutes))
    .map((x) => ({
      ...x,
      capacity: depSeats(x),
      free: depSeats(x),
      allocated: 0,
      outAllocated: 0,
      outRevenue: 0,
    }))

  const nDeps = mkState('Northbound')
  const sDeps = mkState('Southbound')
  const all = [...nDeps, ...sDeps]
  const itins = buildItineraries(net, all)

  // `carried` counts PEOPLE — each originating passenger once, the unit the
  // tills sell in (one admission = one passenger, out and home on one ticket).
  // Until 2026-09-03 the return legs added to it again, so the engine reported
  // passenger LEGS: ~214k for a season the railway sold 125k admissions on, and
  // 160% of the actual sales once the real per-day admissions stood beside it
  // (Chris: "please don't invent mythical new sales"). Seats are still consumed
  // on every leg (fill); only the count changed.
  let revenue = 0, carried = 0, lostSales = 0, unreturned = 0, lostInterchange = 0, connected = 0
  const lostOutbound: { time: string; lost: number; direction?: string }[] = []
  const stranded: StrandedGroup[] = []
  const earlyReturns: { time: string; route: string; count: number; onTime: string; stayMin: number }[] = []
  const stays: StayGroup[] = []
  const oneWay: { time: string; route: string; count: number; asToday?: boolean }[] = []
  const waits: TransferWait[] = []
  const lastMinOpt = opts.lastMin ?? 24 * 60

  // ---- the per-slot ledger: who boarded from each demand slot and whether
  // they got home. A run of TODAY's timetable through this ledger is what
  // decides which slots are one-way today (oneWayShares).
  const slotHome: Record<string, { pax: number; unreturned: number; oneWay: number }> = {}
  const ledger = (k: string) => (slotHome[k] ??= { pax: 0, unreturned: 0, oneWay: 0 })
  const oneWayAcc = new Map<string, { time: string; route: string; count: number; asToday?: boolean }>()
  const addOneWay = (time: string, route: string, count: number, asToday: boolean) => {
    if (count <= 0.001) return
    const k = `${time}|${route}|${asToday ? 'today' : 'window'}`
    const cur = oneWayAcc.get(k)
    if (cur) cur.count += count
    else oneWayAcc.set(k, { time, route, count, ...(asToday ? { asToday: true } : {}) })
  }
  /** Spread a group's stranded / one-way people back over the slots that fed it. */
  const attribute = (parts: Map<string, number>, total: number, field: 'unreturned' | 'oneWay') => {
    let sum = 0
    for (const c of parts.values()) sum += c
    if (sum <= 0) return
    for (const [k, c] of parts) ledger(k)[field] += total * (c / sum)
  }
  /**
   * Can a passenger who rides `it` out get home by rail at all — a train home
   * exists after they arrive, inside the operating window? Existence, not
   * seats: a full train is the allowance's business, a missing one is not.
   */
  const returnable = (it: Itinerary, o: number, d: number): boolean => {
    const soonest = it.arrMin + PASSENGER_MCT_MIN
    if (soonest > lastMinOpt) return false
    const home = itins.get(`${d}>${o}`) ?? []
    return home.some((h) => h.depMin >= soonest)
  }

  const effDwell = opts.maxStay ? PASSENGER_MCT_MIN : dwellMin
  const recordWait = (it: Itinerary, take: number, dir: string) => {
    if (!it.transfer || it.legs.length < 2) return
    connected += take
    waits.push({
      station: it.transferStation,
      inTime: it.legs[0].time,
      outTime: it.legs[1].time,
      count: take,
      waitMin: it.waitMin,
      direction: dir,
    })
  }

  const fill = (it: Itinerary, want: number): number => {
    const take = Math.min(want, freeOn(it))
    if (take <= 0) return 0
    for (const l of it.legs) { l.free -= take; l.allocated += take }
    return take
  }

  // ---- Pass 1: originating demand, both directions. Full fare captured here.
  interface OutGroup { count: number; from: number; to: number; outTime: string; arrival: number; earliest: number; parts: Map<string, number> }
  const outGroups: OutGroup[] = []
  const groupAcc = new Map<string, OutGroup>()

  const outbound = (slots: Slot[], dir: string) => {
    for (const s of slots) {
      const key = `${s.o}>${s.d}`
      const slotKey = `${s.minutes}|${s.o}|${s.d}`
      // A return-only working carries people home; it is not for sale outward.
      const pool = (itins.get(key) ?? []).filter((it) => !it.legs.some((l) => l.returnOnly))
      const hasDirect = pool.some((it) => it.legs.length === 1)
      let remaining = s.paxPerDay * fe

      // Interchange aversion: where the journey CANNOT be made without a
      // change, a share of demand simply doesn't travel. Applied once, up
      // front, so it is visible rather than buried in a utility function.
      if (!hasDirect && pool.length > 0 && pool[0].transfer) {
        const lost = remaining * pool[0].transfer.aversionShare
        lostInterchange += lost
        remaining -= lost
      }

      const near = pool
        .filter((it) => Math.abs(it.depMin - s.minutes) <= toleranceMin)
        .map((it) => ({ it, cost: Math.abs(it.depMin - s.minutes) + it.penalty }))
        .sort((a, b) => a.cost - b.cost)
      // W9 option: rather than losing demand no train within tolerance can
      // take, offer the NEAREST earlier/later departures too — recapture
      // applies per step down the list, so distant substitutes are heavily
      // discounted, and anyone carried out this way still books a return
      // group (the return-ticket invariant holds: carried out ⇒ owed a ride
      // home, loudly unreturned otherwise).
      const far = opts.spillToNearest
        ? pool
          .filter((it) => Math.abs(it.depMin - s.minutes) > toleranceMin)
          .map((it) => ({ it, cost: Math.abs(it.depMin - s.minutes) + it.penalty }))
          .sort((a, b) => a.cost - b.cost)
        : []
      const cands = [...near, ...far]
      const nearN = near.length

      // The slot's ONE-WAY share travels out and makes its own way back, as it
      // does on today's timetable; the rest is owed a ride home.
      const rho = Math.min(1, Math.max(0, s.oneWayShare ?? 0))
      let oneWayRem = remaining * rho
      let retRem = remaining - oneWayRem
      // The share that does not survive a move is a lost sale — counted, never
      // quietly dropped, so "turned away" is the whole of who did not travel.
      let dropped = 0

      for (let i = 0; i < cands.length && oneWayRem + retRem > 0.01; i++) {
        const isFar = i >= nearN
        // Recapture applies to EVERY move away from the wanted departure — the
        // next train within tolerance, and the FIRST train outside it. Until
        // 2026-09-04 the first fallback boarded at full volume, so a lone late
        // train collected a whole day's demand from five hours away untouched
        // (Chris: "it can't be unrealistic").
        if (i > 0 || isFar) {
          dropped += (oneWayRem + retRem) * (1 - recapture)
          oneWayRem *= recapture; retRem *= recapture
        }
        const { it } = cands[i]
        // The model never MOVES a returning passenger onto a train they cannot
        // get home from: they take the latest earlier train they can, or the
        // sale is lost and the optimiser sees it (Chris, 2026-09-04: "get an
        // earlier train"). Within tolerance the train is the passenger's own
        // choice and they travel regardless.
        const canReturn = !isFar || returnable(it, s.o, s.d)
        const want = canReturn ? oneWayRem + retRem : oneWayRem
        if (want <= 0.01) continue
        const take = fill(it, want)
        if (take <= 0) continue
        const takeOW = canReturn ? take * (oneWayRem / (oneWayRem + retRem)) : take
        const takeRet = take - takeOW
        oneWayRem -= takeOW
        retRem -= takeRet
        carried += take
        revenue += take * s.netPerPax * fareMult
        // The fare is booked against the FIRST leg — that is the session the
        // sale is tied to, and it keeps the per-service revenue split honest.
        it.legs[0].outAllocated += take
        it.legs[0].outRevenue += take * s.netPerPax * fareMult
        recordWait(it, take, dir)
        const led = ledger(slotKey)
        led.pax += take
        if (takeOW > 0.001) {
          led.oneWay += takeOW
          addOneWay(it.legs[0].time, routeLabel(net, s.d, s.o), takeOW, true)
        }
        if (takeRet <= 0.001) continue

        const gk = `${s.o}>${s.d}|${it.legs[0].time}`
        const arrival = it.arrMin
        const cur = groupAcc.get(gk)
        if (cur) {
          cur.count += takeRet
          cur.parts.set(slotKey, (cur.parts.get(slotKey) ?? 0) + takeRet)
        } else {
          groupAcc.set(gk, {
            count: takeRet,
            from: s.d,           // journey home is the outward reversed
            to: s.o,
            outTime: it.legs[0].time,
            arrival,
            earliest: arrival + effDwell,
            parts: new Map([[slotKey, takeRet]]),
          })
        }
      }
      remaining = oneWayRem + retRem + dropped
      if (remaining > 0.01) {
        lostSales += remaining
        lostOutbound.push({ time: s.time, lost: remaining, direction: dir })
      }
    }
  }

  outbound(north, 'Northbound')
  outbound(south, 'Southbound')
  outGroups.push(...[...groupAcc.values()].sort((a, b) => a.earliest - b.earliest))

  // ---- Pass 2: returns, both ways, on the same ticket (no extra fare).
  const shapeFor = (from: number, to: number): Slot[] => (to > from ? north : south)

  for (const g of outGroups) {
    const pool = itins.get(`${g.from}>${g.to}`) ?? []
    const dir = g.to > g.from ? 'Northbound' : 'Southbound'
    // A PASSENGER's earliest catchable train home: they step off and cross the
    // platform — minutes, not the locomotive's 30-minute turnaround. Gating
    // this on turnaroundAt stranded people who could see their train home
    // standing at the platform (a 14:20 arrival "couldn't" catch the 14:45),
    // and Best fit then invented rescue trains that breached the NR path
    // budget. The railway's rule: a return booking always comes home.
    const turn = PASSENGER_MCT_MIN

    // Preferred: after the minimum stay. If the last serving journey leaves
    // before the stay is up, people shorten the stay rather than strand
    // themselves — real passengers do not sleep on the platform.
    let cands = pool.filter((it) => it.depMin >= g.earliest)
    if (cands.length === 0) cands = pool.filter((it) => it.depMin >= g.arrival + turn)

    if (cands.length === 0) {
      // Fixable, or genuinely one-way? A rescue train can be added any time
      // from arrival + turnaround up to the last-train window. Only when even
      // that is impossible are these people one-way/overnight travellers.
      const soonest = g.arrival + turn
      if (soonest > lastMinOpt) {
        addOneWay(g.outTime, routeLabel(net, g.from, g.to), g.count, false)
        attribute(g.parts, g.count, 'oneWay')
      } else {
        unreturned += g.count
        attribute(g.parts, g.count, 'unreturned')
        stranded.push({
          time: g.outTime,
          route: routeLabel(net, g.from, g.to),
          o: g.from,
          d: g.to,
          count: g.count,
          earliestMin: g.earliest,
          arrivalMin: g.arrival,
          reason: `no journey serving ${routeLabel(net, g.from, g.to)} departs after ${fmtMin(soonest)} — one can be added inside the operating window`,
        })
      }
      continue
    }

    let want = g.count
    const recordStay = (it: Itinerary, take: number) => {
      stays.push({
        time: g.outTime,
        outRoute: routeLabel(net, g.to, g.from),
        retTime: it.legs[0].time,
        count: take,
        stayMin: it.depMin - g.arrival,
      })
      recordWait(it, take, dir)
    }

    if (returnMode === 'latest') {
      // Everyone aims for the LAST journey home; spill backwards as it fills.
      // Among equal departure times prefer the cheapest (direct over a change).
      const order = [...cands].sort((a, b) => (b.depMin - a.depMin) || (a.penalty - b.penalty))
      for (const it of order) {
        if (want <= 0.01) break
        const take = fill(it, want)
        // The return leg seats the SAME people counted on the way out — it
        // fills capacity but is not another passenger (see `carried` below).
        if (take > 0) { want -= take; recordStay(it, take) }
      }
    } else {
      let shape = shapeFor(g.from, g.to).filter((s) => s.minutes >= g.earliest)
      if (shape.length === 0) {
        shape = [{ minutes: g.earliest, time: '', paxPerDay: 1, netPerPax: 0, o: g.from, d: g.to, route: '' }]
      }
      const wTotal = shape.reduce((t, s) => t + s.paxPerDay, 0) || 1
      for (const s of shape) {
        let sw = g.count * (s.paxPerDay / wTotal)
        const cc = [...cands].sort(
          (a, b) => (Math.abs(a.depMin - s.minutes) + a.penalty) - (Math.abs(b.depMin - s.minutes) + b.penalty)
        )
        for (const it of cc) {
          if (sw <= 0.01) break
          const take = fill(it, sw)
          if (take > 0) { sw -= take; want -= take; recordStay(it, take) }
        }
      }
    }

    if (want > 0.01 && opts.forceEarly) {
      const earlier = pool
        .filter((it) => it.depMin >= g.arrival + turn && it.depMin < g.earliest)
        .sort((a, b) => b.depMin - a.depMin)
      for (const it of earlier) {
        if (want <= 0.01) break
        const take = fill(it, want)
        if (take > 0) {
          want -= take
          earlyReturns.push({
            time: g.outTime,
            route: routeLabel(net, g.from, g.to),
            count: take,
            onTime: it.legs[0].time,
            stayMin: it.depMin - g.arrival,
          })
          recordStay(it, take)
        }
      }
    }

    if (want > 0.01) {
      unreturned += want
      attribute(g.parts, want, 'unreturned')
      stranded.push({
        time: g.outTime,
        route: routeLabel(net, g.from, g.to),
        o: g.from,
        d: g.to,
        count: want,
        earliestMin: g.earliest,
        arrivalMin: g.arrival,
        reason: 'every serving journey home is full',
      })
    }
  }
  oneWay.push(...[...oneWayAcc.values()].sort((a, b) => toMin(a.time) - toMin(b.time)))

  const perDep: PerDep[] = all
    .map((d) => ({
      id: d.id,
      direction: d.direction,
      time: d.time,
      capacity: d.capacity,
      allocated: d.allocated,
      outAlloc: d.outAllocated,
      retAlloc: d.allocated - d.outAllocated,
      outRevenue: d.outRevenue,
    }))
    .sort((a, b) => toMin(a.time) - toMin(b.time))

  return {
    revenue,
    carried,
    lostSales,
    unreturned,
    lostInterchange,
    connected,
    perDep,
    lostOutbound,
    stranded,
    earlyReturns,
    stays,
    oneWay,
    transfers: summariseTransfers(net, waits),
    slotHome,
  }
}

/** A demand slot's ledger key — the same identity blendParts matches slots on. */
export const slotKeyOf = (s: { minutes: number; o: number; d: number }): string => `${s.minutes}|${s.o}|${s.d}`

/**
 * WHO IS ONE-WAY TODAY — the one owner (Chris, 2026-09-04: "anyone who
 * currently travels on an unreturning train, you can just assume and carry
 * over their sales").
 *
 * Runs TODAY's timetable against a day-type's demand and returns, per slot,
 * the share of its passengers who did not get a seat home: no train after they
 * arrived, or every train home full, or arriving after the last train. The
 * railway sold them that journey and they made their own way back, so in every
 * design they travel one way, keep their fare, and are never stranded. The
 * remaining share is owed a ride home like everyone else.
 *
 * Stamped shares are stripped before the run, so calling this on an already
 * stamped class gives the same answer.
 */
export function oneWayShares(net: Network, todayDeps: Departure[], cls: ClassDemand, p: EngineParams): Record<string, number> {
  const strip = (slots: Slot[]): Slot[] => slots.map((s) => (s.oneWayShare == null ? s : { ...s, oneWayShare: undefined }))
  const sc = runScenario(net, strip(cls.north), strip(cls.south), todayDeps.filter((d) => d.enabled), p)
  const out: Record<string, number> = {}
  for (const [k, v] of Object.entries(sc.slotHome)) {
    if (v.pax <= 0.001) continue
    const share = Math.min(1, (v.unreturned + v.oneWay) / v.pax)
    if (share > 0.001) out[k] = share
  }
  return out
}

/** Stamp one-way-today shares onto a day-type's slots (a copy; the input is untouched). */
export function stampOneWay(cls: ClassDemand, shares: Record<string, number> | undefined): ClassDemand {
  if (!shares) return cls
  const stamp = (slots: Slot[]): Slot[] => slots.map((s) => {
    const sh = shares[slotKeyOf(s)]
    return sh != null && sh > 0 ? { ...s, oneWayShare: sh } : s
  })
  return { ...cls, north: stamp(cls.north), south: stamp(cls.south) }
}

/** One-way passengers a day carried over from today's timetable, for a scenario. */
export const oneWayAsToday = (sc: Scenario): number => sc.oneWay.filter((x) => x.asToday).reduce((t, x) => t + x.count, 0)

// ---------------------------------------------------------------------------
//  Transfer queue
//
//  Grosmont in Mode B is not a capacity limit like a train — it is a BUFFER
//  with an arrival process (main-line arrivals) and a service process (shuttle
//  departures). A full shuttle does not turn people away, it delays them; and
//  on the return leg a missed connection becomes an unreturned passenger. So
//  what matters operationally is the peak population on the platform and how
//  long they stand there.
// ---------------------------------------------------------------------------

export function summariseTransfers(net: Network, waits: TransferWait[]): TransferSummary[] {
  if (waits.length === 0) return []
  const out: TransferSummary[] = []
  for (const node of net.transfers) {
    const mine = waits.filter((w) => w.station === node.station && w.count > 0.01)
    if (mine.length === 0) continue

    // Sweep the arrival/departure events to find the peak waiting population.
    const events: { t: number; delta: number; loads: number }[] = []
    for (const w of mine) {
      const inAt = toMin(w.outTime) - w.waitMin
      events.push({ t: inAt, delta: w.count, loads: 1 })
      events.push({ t: toMin(w.outTime), delta: -w.count, loads: -1 })
    }
    events.sort((a, b) => a.t - b.t || a.delta - b.delta)
    let cur = 0, peak = 0, peakAt = 0, loads = 0, peakLoads = 0
    for (const e of events) {
      cur += e.delta
      loads += e.loads
      if (cur > peak) { peak = cur; peakAt = e.t }
      if (loads > peakLoads) peakLoads = loads
    }

    const total = mine.reduce((s, w) => s + w.count, 0)
    const meanWait = total > 0 ? mine.reduce((s, w) => s + w.count * w.waitMin, 0) / total : 0

    out.push({
      station: node.station,
      transferred: total,
      peakWaiting: peak,
      peakAtMin: peakAt,
      meanWaitMin: meanWait,
      peakTrainloads: peakLoads,
      platforms: node.platforms,
      waits: mine.sort((a, b) => toMin(a.outTime) - toMin(b.outTime)),
    })
  }
  return out
}

// ---------------------------------------------------------------------------
//  Multi-day-class evaluation
//
//  A ClassDemand carries a WEIGHT = days per week it represents. runClasses
//  scores ONE timetable against every class and returns weighted totals per
//  period — except perDep.allocated, normalised back to an average running day
//  so the editor's load bars stay meaningful.
// ---------------------------------------------------------------------------

export function runClasses(
  net: Network,
  classes: ClassDemand[],
  deps: Departure[],
  p: EngineParams
): Scenario {
  if (classes.length === 1 && classes[0].weight === 1) {
    return runScenario(net, classes[0].north, classes[0].south, deps, p)
  }
  const perDep = new Map<string, PerDep>()
  const lost = new Map<string, number>()
  const strandedMap = new Map<string, StrandedGroup>()
  const earlyMap = new Map<string, { time: string; route: string; count: number; onTime: string; stayMin: number }>()
  const staysMap = new Map<string, StayGroup>()
  const oneWayMap = new Map<string, { time: string; route: string; count: number; asToday?: boolean }>()
  const waitMap = new Map<string, TransferWait>()
  const slotHomeAcc: Record<string, { pax: number; unreturned: number; oneWay: number }> = {}
  let revenue = 0, carried = 0, lostSales = 0, unreturned = 0, lostInterchange = 0, connected = 0

  for (const c of classes) {
    const r = runScenario(net, c.north, c.south, deps, p)
    revenue += r.revenue * c.weight
    carried += r.carried * c.weight
    lostSales += r.lostSales * c.weight
    unreturned += r.unreturned * c.weight
    lostInterchange += r.lostInterchange * c.weight
    connected += r.connected * c.weight
    for (const d of r.perDep) {
      const cur = perDep.get(d.id) ?? { ...d, allocated: 0, outAlloc: 0, retAlloc: 0, outRevenue: 0 }
      cur.allocated += d.allocated * c.weight
      cur.outAlloc += d.outAlloc * c.weight
      cur.retAlloc += d.retAlloc * c.weight
      cur.outRevenue += d.outRevenue * c.weight
      perDep.set(d.id, cur)
    }
    for (const l of r.lostOutbound) lost.set(l.time, (lost.get(l.time) ?? 0) + l.lost * c.weight)
    for (const g of r.stranded) {
      const k = `${g.time}|${g.route}|${g.reason}`
      const cur = strandedMap.get(k)
      if (cur) cur.count += g.count * c.weight
      else strandedMap.set(k, { ...g, count: g.count * c.weight })
    }
    for (const e of r.earlyReturns) {
      const k = `${e.time}|${e.route}|${e.onTime}`
      const cur = earlyMap.get(k)
      if (cur) cur.count += e.count * c.weight
      else earlyMap.set(k, { ...e, count: e.count * c.weight })
    }
    for (const st of r.stays) {
      const k = `${st.time}|${st.outRoute}|${st.retTime}`
      const cur = staysMap.get(k)
      if (cur) cur.count += st.count * c.weight
      else staysMap.set(k, { ...st, count: st.count * c.weight })
    }
    for (const ow of r.oneWay) {
      const k = `${ow.time}|${ow.route}|${ow.asToday ? 'today' : 'window'}`
      const cur = oneWayMap.get(k)
      if (cur) cur.count += ow.count * c.weight
      else oneWayMap.set(k, { ...ow, count: ow.count * c.weight })
    }
    for (const [k, v] of Object.entries(r.slotHome)) {
      const cur = slotHomeAcc[k] ??= { pax: 0, unreturned: 0, oneWay: 0 }
      cur.pax += v.pax * c.weight; cur.unreturned += v.unreturned * c.weight; cur.oneWay += v.oneWay * c.weight
    }
    for (const s of r.transfers) {
      for (const w of s.waits) {
        const k = `${w.station}|${w.inTime}|${w.outTime}|${w.direction}`
        const cur = waitMap.get(k)
        if (cur) cur.count += w.count * c.weight
        else waitMap.set(k, { ...w, count: w.count * c.weight })
      }
    }
  }

  const w = classes.reduce((t, c) => t + c.weight, 0) || 1
  return {
    revenue,
    carried,
    lostSales,
    unreturned,
    lostInterchange,
    connected,
    perDep: [...perDep.values()]
      .map((d) => ({
        ...d,
        allocated: d.allocated / w,
        outAlloc: d.outAlloc / w,
        retAlloc: d.retAlloc / w,
        outRevenue: d.outRevenue / w,
      }))
      .sort((a, b) => toMin(a.time) - toMin(b.time)),
    lostOutbound: [...lost.entries()]
      .map(([time, l]) => ({ time, lost: l }))
      .sort((a, b) => toMin(a.time) - toMin(b.time)),
    stranded: [...strandedMap.values()].sort((a, b) => toMin(a.time) - toMin(b.time)),
    earlyReturns: [...earlyMap.values()].sort((a, b) => toMin(a.time) - toMin(b.time)),
    stays: [...staysMap.values()].sort(
      (a, b) => toMin(a.time) - toMin(b.time) || toMin(a.retTime) - toMin(b.retTime)
    ),
    oneWay: [...oneWayMap.values()].sort((a, b) => toMin(a.time) - toMin(b.time)),
    transfers: summariseTransfers(net, [...waitMap.values()]),
    slotHome: slotHomeAcc,
  }
}

/** The observed timetable for a slot set — the Studio's baseline departures. */
export function baselineDeps(net: Network, north: Slot[], south: Slot[], coaches = 7): Departure[] {
  const uniq = new Map<string, Departure>()
  for (const [dir, arr] of [['Northbound', north], ['Southbound', south]] as const) {
    for (const s of arr) {
      const k = `${dir}-${s.time}-${s.route}`
      if (uniq.has(k)) continue
      uniq.set(k, {
        id: `b-${k}`,
        direction: dir,
        time: s.time,
        minutes: s.minutes,
        enabled: true,
        coaches,
        o: s.o,
        d: s.d,
        group: groupKeyFor(net, s.o, s.d),
      })
    }
  }
  return [...uniq.values()].sort((a, b) => a.minutes - b.minutes)
}
